import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

// eslint-disable-next-line import/no-unresolved
import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax
import 'mapbox-gl/dist/mapbox-gl.css';
import '../../styles/modules/mapbox-custom-styles.scss';

import drawMap from '../../lib/drawMapbox';
import add2dLayer from '../../lib/add2dLayer';
import createLocationList from '../../lib/createLocationList';
import mapInteractions from '../../utils/mapInteractions';

mapboxgl.accessToken = 'pk.eyJ1IjoiYm9zdG9uZ2xvYmUtbWFwYm94IiwiYSI6ImNrMXMzdzRjdzBjMTUzYmxnYWFicjk3dWkifQ.Pqincssv6-WMTdbuGiAyHw';

const MapContainer = styled.div`
  position: relative;
`;

const Map = styled.div`
  width: 100%;
  height: 66vw;
  max-height: 90vh;
  margin: 2rem 0 0;
  @media(max-width: 800px) {
    height: 100vw;
    min-height: 400px;
  }
`;

const MapToggle = styled.button`
  background-color: var(--globe-red);
  color: white;
  border: none;
  border-radius: 3px;
  position: absolute;
  top: 10px;
  left: 10px;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  transition: all 0.1s ease-out;
  &:hover {
    color: var(--offblack);
    background-color: white;
  }
`;

const Mapbox = ({
  setMasterSnowFall,
  modifiedSnowFall,
  selectedState,
  setLocations,
  mapRef,
  mapContainer,
  mapView,
  setMapView,
  mapLoaded,
  setMapLoaded,
}) => {
  // eslint-disable-next-line
  // test data
  // const [dataUrl, setDataUrl] = useState('https://apps.bostonglobe.com/newsapps/data/snow-totals/test-data/snow-data-geo-01-31-refactored.json');
  // live data
  const [dataUrl, setDataUrl] = useState('https://apps.bostonglobe.com/newsapps/data/snow-totals/v2/snow-data-geo.json');

  // ----------------------------------------------------------------------
  // INITIAL MAP SETUP
  // Handles creating the mapbox map object, disabling zoom with scroll,
  // and adding new controls. Note, this does not run after mapRef.current
  // has been assigned the new mapbox map object
  // ----------------------------------------------------------------------

  useEffect(() => {
    if (mapRef.current) return;

    // eslint-disable-next-line
    mapRef.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/bostonglobe-mapbox/ckkekr3w71cqq17mq89xv3hhl',
      center: [-71.7, 42.36],
      zoom: 7.5,
      pitch: 60,
      bearing: -30,
    });

    mapRef.current.scrollZoom.disable();
    mapRef.current.addControl(new mapboxgl.NavigationControl());
  }, []);

  // ----------------------------------------------------------------------
  // FETCHING THE DATA AND DRAWING THE INITIAL LAYER
  // If the map object exists, fetcch the data, set that data in state to
  // the masterSnowFall property, create an initial list of locations based
  // on the selectedState, and draw the intiial layer. After the map is loaded,
  // set the mapLoaded property in state to true
  // ----------------------------------------------------------------------

  useEffect(() => {
    if (mapRef.current) {
      fetch(dataUrl)
        .then((response) => response.json())
        .then((data) => {
          setMasterSnowFall(data);
          setLocations(createLocationList(data, selectedState));
          mapRef.current.on('load', () => {
            drawMap(mapRef.current, data.data);
            setMapLoaded(true);
          });
        });
    }
  }, [dataUrl]);

  // ----------------------------------------------------------------------
  // TOGGLING THE MAP VIEW
  // Sets up a listener that toggles the map view between 2D/3D depending on
  // the mapView property value in state
  // ----------------------------------------------------------------------

  useEffect(() => {
    mapInteractions.toggleMapView(mapRef.current, mapView);
  }, [mapView]);

  // ----------------------------------------------------------------------
  // DRAWING THE 2D LAYER
  // After the mapLoaded property is set to true, draws the 2D layer on the map
  // ----------------------------------------------------------------------

  useEffect(() => {
    if (mapLoaded) {
      add2dLayer(mapRef.current, modifiedSnowFall.data);
    }
  }, [modifiedSnowFall.data, mapLoaded]);

  // ----------------------------------------------------------------------
  // SETTING THE 2D/3D MAPVIEW STATE
  // When the map view toggle is clicked, sets the mapView state with new value
  // ----------------------------------------------------------------------

  const toggleView = useCallback(() => {
    const newMapView = mapView === '2d' ? '3d' : '2d';
    setMapView(newMapView);
  }, [mapView]);

  return (

    <MapContainer id="map-container">
      <Map id="map" ref={mapContainer} />
      {modifiedSnowFall.data
        && (
          <MapToggle onClick={() => toggleView()} type="button" className="bentonsanscond-n4">
            {mapView === '2d' ? 'View in 3-D' : 'View in 2-D'}
          </MapToggle>
        )}
    </MapContainer>
  );
};

export default Mapbox;

Mapbox.propTypes = {
  setMasterSnowFall: PropTypes.func.isRequired,
  modifiedSnowFall: PropTypes.shape({
    data: PropTypes.shape({}),
  }).isRequired,
  selectedState: PropTypes.string.isRequired,
  setLocations: PropTypes.func.isRequired,
  mapRef: PropTypes.shape({
    current: PropTypes.shape({
      addControl: PropTypes.func.isRequired,
      on: PropTypes.func.isRequired,
      scrollZoom: PropTypes.shape({
        disable: PropTypes.func.isRequired,
      }).isRequired,
    }),
  }).isRequired,
  mapContainer: PropTypes.shape({
    current: PropTypes.shape({}),
  }).isRequired,
  mapView: PropTypes.string.isRequired,
  setMapView: PropTypes.func.isRequired,
  mapLoaded: PropTypes.bool.isRequired,
  setMapLoaded: PropTypes.func.isRequired,
};
